/**
 * Pure parsing + MissingInfo predicates for LedgerVue `DecimalValueDto`-shaped JSON.
 *
 * - Does **not** coerce absent DTOs to `0`, apply prior-business-day pricing, or interpret calendars.
 * - Valuation semantics and staleness belong on the server; the web client surfaces API payloads.
 *
 * Parity intent: WPF `DecimalValueViewModel` expander exposes missing FX/price lists — React grids will
 * use these predicates in Phase 2. See plans/Post-Reports-DecimalValueDto-layer.md
 */

import { dateStringOrNull } from './formatters';

export { dateStringOrNull };

export interface DecimalValueDto {
  value?: number | null;
  /** Some serializers emit PascalCase alongside camelCase. */
  Value?: number | null;
  missingPrices?: unknown[] | null;
  missingFxRates?: unknown[] | null;
  MissingPrices?: unknown[] | null;
  MissingFxRates?: unknown[] | null;
  displayFormat?: string | null;
  DisplayFormat?: string | null;
  effectiveDate?: string | null;
  EffectiveDate?: string | null;
}

function asRecord(dto: unknown): Record<string, unknown> | null {
  if (dto == null || typeof dto !== 'object' || Array.isArray(dto)) return null;
  return dto as Record<string, unknown>;
}

function missingArrayLength(o: Record<string, unknown>, camelKey: string, pascalKey: string): number {
  const raw = o[camelKey] ?? o[pascalKey];
  return Array.isArray(raw) ? raw.length : 0;
}

/**
 * True when the payload carries a non-empty `missingPrices` / `MissingPrices` array.
 */
export function hasMissingPrice(dto: unknown): boolean {
  const o = asRecord(dto);
  return o != null && missingArrayLength(o, 'missingPrices', 'MissingPrices') > 0;
}

/**
 * True when the payload carries a non-empty `missingFxRates` / `MissingFxRates` array.
 */
export function hasMissingFx(dto: unknown): boolean {
  const o = asRecord(dto);
  return o != null && missingArrayLength(o, 'missingFxRates', 'MissingFxRates') > 0;
}

/**
 * True when a finite numeric `value` / `Value` can be read (after unwrapping nested DTOs).
 */
export function isPresentNumeric(dto: unknown): boolean {
  return numericValueOrNull(dto) !== null;
}

/**
 * Reads a finite number from primitives, numeric strings, or nested `{ value }` / `{ Value }` DTOs.
 * Returns `null` for missing, non-finite, or non-numeric payloads — never `0` as a stand-in for “unknown”.
 */
export function numericValueOrNull(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    return numericValueOrNull(o.value ?? o.Value);
  }
  return null;
}

/**
 * @deprecated Prefer {@link numericValueOrNull}; kept for incremental migration of report/holding helpers.
 */
export function decimalDtoValue(raw: unknown): number | null {
  return numericValueOrNull(raw);
}

/**
 * Optional `effectiveDate` / `EffectiveDate` from API `DecimalValueDto` JSON.
 *
 * `DecimalValueDto.EffectiveDate` is typed as the `LedgerVue.Core.Date` value object rather than
 * `DateTime`, so Newtonsoft emits `{ dateTime, year, month, day, ... }` instead of a string —
 * `dateStringOrNull` handles both shapes.
 */
export function effectiveDateFromDto(dto: unknown): string | null {
  const o = asRecord(dto);
  if (o == null) return null;
  return dateStringOrNull(o.effectiveDate ?? o.EffectiveDate);
}

/**
 * Detail parsers for `MissingPriceInfo` / `MissingFxRateInfo` (`Shared/LedgerVue.Core`), enabling
 * the WPF-parity capture popover (list the gaps, let the user enter + save the missing value).
 *
 * Server shape (Newtonsoft, `CamelCasePropertyNamesContractResolver` + `StringEnumConverter` +
 * `NullValueHandling.Ignore` — see `Server/LedgerVue.Server/Program.cs`):
 *   missingPrices:   [{ date, holdingKey: { instrument: { symbol, namespace }, contractParameters: [{ name, value, parameterType }] } }]
 *   missingFxRates:  [{ date, baseCurrency, counterCurrency, dealId? }]  (dealId omitted, not null, when absent)
 * `MissingPriceInfo.Symbol` / `.ContractParameters` are `[JsonIgnore]` on the server — only `Date`
 * and `HoldingKey` are ever serialized, so the symbol must be read via `holdingKey.instrument.symbol`.
 */

export interface MissingPriceContractParameter {
  name: string;
  value: unknown;
  /** `ContractParameterType` enum, serialized as a string ("Date" | "Decimal") via `StringEnumConverter`. */
  parameterType: string;
}

export interface MissingPriceItem {
  /** ISO date string as emitted by the server (`DateFormatHandling.IsoDateFormat`). */
  date: string;
  symbol: string;
  namespace?: string;
  contractParameters: MissingPriceContractParameter[];
}

export interface MissingFxRateItem {
  date: string;
  baseCurrency: string;
  counterCurrency: string;
  dealId?: string | null;
}

function stringOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asArray(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

function parseContractParameter(raw: unknown): MissingPriceContractParameter | null {
  const o = asRecord(raw);
  if (o == null) return null;
  const name = stringOrEmpty(o.name ?? o.Name);
  if (!name) return null;
  return {
    name,
    value: o.value ?? o.Value,
    parameterType: stringOrEmpty(o.parameterType ?? o.ParameterType) || 'Decimal',
  };
}

function parseMissingPriceItem(raw: unknown): MissingPriceItem | null {
  const o = asRecord(raw);
  if (o == null) return null;
  const holdingKey = asRecord(o.holdingKey ?? o.HoldingKey);
  const instrument = asRecord(holdingKey?.instrument ?? holdingKey?.Instrument);
  const symbol = stringOrEmpty(instrument?.symbol ?? instrument?.Symbol);
  if (!symbol) return null;
  const namespaceValue = instrument?.namespace ?? instrument?.Namespace;
  const contractParameters = asArray(holdingKey?.contractParameters ?? holdingKey?.ContractParameters)
    .map(parseContractParameter)
    .filter((p): p is MissingPriceContractParameter => p != null);
  return {
    date: stringOrEmpty(o.date ?? o.Date),
    symbol,
    namespace: typeof namespaceValue === 'string' ? namespaceValue : undefined,
    contractParameters,
  };
}

function parseMissingFxRateItem(raw: unknown): MissingFxRateItem | null {
  const o = asRecord(raw);
  if (o == null) return null;
  const baseCurrency = stringOrEmpty(o.baseCurrency ?? o.BaseCurrency);
  const counterCurrency = stringOrEmpty(o.counterCurrency ?? o.CounterCurrency);
  if (!baseCurrency || !counterCurrency) return null;
  const dealIdValue = o.dealId ?? o.DealId;
  return {
    date: stringOrEmpty(o.date ?? o.Date),
    baseCurrency,
    counterCurrency,
    dealId: typeof dealIdValue === 'string' ? dealIdValue : null,
  };
}

/** Structured `MissingPriceInfo` rows from a `DecimalValueDto`-shaped payload's `missingPrices` array. */
export function missingPricesFromDto(dto: unknown): MissingPriceItem[] {
  const o = asRecord(dto);
  if (o == null) return [];
  return asArray(o.missingPrices ?? o.MissingPrices)
    .map(parseMissingPriceItem)
    .filter((item): item is MissingPriceItem => item != null);
}

/** Structured `MissingFxRateInfo` rows from a `DecimalValueDto`-shaped payload's `missingFxRates` array. */
export function missingFxRatesFromDto(dto: unknown): MissingFxRateItem[] {
  const o = asRecord(dto);
  if (o == null) return [];
  return asArray(o.missingFxRates ?? o.MissingFxRates)
    .map(parseMissingFxRateItem)
    .filter((item): item is MissingFxRateItem => item != null);
}

export interface MissingInfoCollection {
  prices: MissingPriceItem[];
  fxRates: MissingFxRateItem[];
}

/**
 * Report-level aggregate of every distinct missing price / FX rate across a set of
 * `DecimalValueDto`-shaped row values — mirrors WPF's `MissingInfoAggregatorExpander` (dedupe by
 * date+symbol / date+currency-pair, sorted by date) for the summary bar shown under a grid.
 */
export function collectMissingInfo(dtos: unknown[]): MissingInfoCollection {
  const pricesByKey = new Map<string, MissingPriceItem>();
  const fxRatesByKey = new Map<string, MissingFxRateItem>();

  for (const dto of dtos) {
    for (const price of missingPricesFromDto(dto)) {
      const key = `${price.date}|${price.namespace ?? ''}:${price.symbol}`;
      if (!pricesByKey.has(key)) pricesByKey.set(key, price);
    }
    for (const fxRate of missingFxRatesFromDto(dto)) {
      const key = `${fxRate.date}|${fxRate.baseCurrency}/${fxRate.counterCurrency}`;
      if (!fxRatesByKey.has(key)) fxRatesByKey.set(key, fxRate);
    }
  }

  return {
    prices: Array.from(pricesByKey.values()).sort((a, b) => a.date.localeCompare(b.date)),
    fxRates: Array.from(fxRatesByKey.values()).sort((a, b) => a.date.localeCompare(b.date)),
  };
}
